import React, { useState, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { adminService } from '../../services/adminService';
import { useAuth } from '../auth/hooks/useAuth';
import { Modal } from '../shared/Modal';
import { useAbortableFetch } from '../../hooks/useAbortableFetch';
import { mapSupabaseError } from '../../utils/supabaseErrors';
import './AdminManagement.css';

const REGULAR_ROLES = ['Supervisor', 'Member'];

export function AdminManagement() {
  const { userRole } = useAuth();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  const [promoteUser, setPromoteUser] = useState(null);
  const [promoteModalOpen, setPromoteModalOpen] = useState(false);
  const [promoteToRole, setPromoteToRole] = useState('');

  const [demoteUser, setDemoteUser] = useState(null);
  const [demoteModalOpen, setDemoteModalOpen] = useState(false);
  const [demoteToRole, setDemoteToRole] = useState('');

  const [actionLoading, setActionLoading] = useState(false);

  useAbortableFetch((ctx) => fetchUsers(ctx), []);

  const filteredUsers = useMemo(() => {
    let filtered = users;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(u =>
        u.email?.toLowerCase().includes(term) || u.name?.toLowerCase().includes(term)
      );
    }

    if (roleFilter) {
      if (roleFilter === 'regular') {
        filtered = filtered.filter(u => REGULAR_ROLES.includes(u.role));
      } else {
        filtered = filtered.filter(u => u.role === roleFilter);
      }
    }

    return filtered;
  }, [users, searchTerm, roleFilter]);

  // Admin-only access - placed AFTER all hooks
  if (userRole?.role !== 'Admin') {
    return <Navigate to="/unauthorized" />;
  }

  const fetchUsers = async (ctx = {}) => {
    const isCancelled = ctx.isCancelled ?? (() => false);
    try {
      setLoading(true);
      setError('');
      const allUsers = await adminService.getAllUsers();
      if (isCancelled()) return;
      setUsers(allUsers);
    } catch (err) {
      if (isCancelled()) return;
      setError('Failed to load users');
    } finally {
      if (isCancelled()) return;
      setLoading(false);
    }
  };

  const sortUsersByRole = (usersToSort) => {
    // Sort: Admins first, then Managers, then Supervisors, then Members
    return usersToSort.sort((a, b) => {
      const roleOrder = { 'Admin': 0, 'Manager': 1, 'Supervisor': 2, 'Member': 3 };
      return roleOrder[a.role] - roleOrder[b.role];
    });
  };

  const getAdmins = () => sortUsersByRole(filteredUsers.filter(u => u.role === 'Admin'));
  const getManagers = () => sortUsersByRole(filteredUsers.filter(u => u.role === 'Manager'));
  const getRegularUsers = () => sortUsersByRole(filteredUsers.filter(u => REGULAR_ROLES.includes(u.role)));

  const handlePromoteClick = (user) => {
    setPromoteUser(user);
    setPromoteToRole('');
    setPromoteModalOpen(true);
  };

  const handleConfirmPromote = async () => {
    if (!promoteToRole) {
      setError('Please select a role');
      return;
    }

    try {
      setActionLoading(true);
      setError('');
      if (promoteToRole === 'Admin') {
        await adminService.promoteToAdmin(promoteUser.id);
      } else {
        await adminService.promoteToManager(promoteUser.id);
      }

      setUsers(prev => prev.map(u => u.id === promoteUser.id ? { ...u, role: promoteToRole } : u));
      const promotedEmail = promoteUser.email;
      setPromoteModalOpen(false);
      setPromoteUser(null);
      setPromoteToRole('');
      toast.success(`Promoted ${promotedEmail} to ${promoteToRole}`);
    } catch (err) {
      setError('Failed to promote user: ' + mapSupabaseError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDemoteClick = (user) => {
    setDemoteUser(user);
    setDemoteToRole('');
    setDemoteModalOpen(true);
  };

  const handleConfirmDemote = async () => {
    if (!demoteToRole) {
      setError('Please select a role');
      return;
    }

    try {
      setActionLoading(true);
      setError('');
      await adminService.demoteUser(demoteUser.id, demoteToRole);

      setUsers(prev => prev.map(u => u.id === demoteUser.id ? { ...u, role: demoteToRole } : u));
      const demotedEmail = demoteUser.email;
      setDemoteModalOpen(false);
      setDemoteUser(null);
      setDemoteToRole('');
      toast.success(`Demoted ${demotedEmail} to ${demoteToRole}`);
    } catch (err) {
      setError('Failed to demote user: ' + mapSupabaseError(err));
    } finally {
      setActionLoading(false);
    }
  };

  const renderUserSection = (sectionUsers, sectionTitle, sectionIcon, canPromote) => {
    if (sectionUsers.length === 0) return null;

    return (
      <tbody key={sectionTitle} className="admin-mgmt-section">
        <tr className="admin-mgmt-section-row">
          <td colSpan={5}>{sectionIcon} {sectionTitle}</td>
        </tr>
        {sectionUsers.map(user => (
          <tr key={user.id}>
            <td>{user.email}</td>
            <td>{user.name}</td>
            <td>
              <span className={`badge badge-${user.role.toLowerCase().replace(/\s+/g, '-')}`}>
                {user.role}
              </span>
            </td>
            <td>
              <span className={`badge badge-${user.status}`}>
                {user.status === 'active' ? 'Active' : 'Inactive'}
              </span>
            </td>
            <td>
              <div className="admin-mgmt-actions">
                {canPromote ? (
                  <button className="btn btn-promote" onClick={() => handlePromoteClick(user)} disabled={actionLoading}>
                    Promote
                  </button>
                ) : (
                  <button className="btn btn-demote" onClick={() => handleDemoteClick(user)} disabled={actionLoading}>
                    Demote
                  </button>
                )}
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    );
  };

  if (loading) return <div className="admin-mgmt-loading">Loading users...</div>;

  const admins = getAdmins();
  const managers = getManagers();
  const regularUsers = getRegularUsers();

  return (
    <div className="admin-management">
      {error && <div className="admin-mgmt-error">{error}</div>}

      <div className="admin-mgmt-controls">
        <input
          type="text"
          className="admin-mgmt-search"
          placeholder="Search by email or name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          className="admin-mgmt-filter"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
        >
          <option value="">All Roles</option>
          <option value="Admin">Admin</option>
          <option value="Manager">Manager</option>
          <option value="regular">Regular Users</option>
        </select>
      </div>

      <div className="admin-mgmt-tables-container">
        <table className="admin-mgmt-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Current Role</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          {renderUserSection(admins, 'ADMINS (Full Access)', '👤', false)}
          {renderUserSection(managers, 'MANAGERS (Limited Admin Access)', '⚙️', false)}
          {renderUserSection(regularUsers, 'REGULAR USERS (Promotable)', '👥', true)}
        </table>

        {filteredUsers.length === 0 && (
          <div className="admin-mgmt-no-data">No users found</div>
        )}
      </div>

      {/* Promote User Modal */}
      <Modal isOpen={promoteModalOpen} onClose={() => setPromoteModalOpen(false)}>
        <div className="admin-mgmt-modal">
          <h2>Promote User to Admin/Manager</h2>
          {promoteUser && <p className="admin-mgmt-modal-subtitle">User: <strong>{promoteUser.email}</strong></p>}

          <div className="role-info">
            <strong>🔓 Admin - Full Access</strong>
            <p>Can manage users, KPI systems, registrations, and promote/demote other admins and managers.</p>
          </div>

          <div className="role-info">
            <strong>⚙️ Manager - Limited Admin Access</strong>
            <p>Can manage regular users only. Cannot manage admins, managers, or change their own role.</p>
          </div>

          <div className="form-group">
            <label htmlFor="promote-role-select">Select Role</label>
            <select
              id="promote-role-select"
              value={promoteToRole}
              onChange={(e) => { setPromoteToRole(e.target.value); if (error) setError(''); }}
            >
              <option value="">-- Select a role --</option>
              <option value="Admin">Promote to Admin</option>
              <option value="Manager">Promote to Manager</option>
            </select>
          </div>

          {promoteToRole && (
            <div className="admin-mgmt-warning">
              ⚠️ Promoting this user will grant them administrative privileges.
            </div>
          )}

          <div className="admin-mgmt-modal-actions">
            <button className="btn btn-secondary" onClick={() => setPromoteModalOpen(false)} disabled={actionLoading}>Cancel</button>
            <button className="btn btn-primary" onClick={handleConfirmPromote} disabled={actionLoading}>Promote User</button>
          </div>
        </div>
      </Modal>

      {/* Demote User Modal */}
      <Modal isOpen={demoteModalOpen} onClose={() => setDemoteModalOpen(false)}>
        <div className="admin-mgmt-modal">
          <h2>Demote User</h2>
          {demoteUser && <p className="admin-mgmt-modal-subtitle">User: <strong>{demoteUser.email}</strong></p>}

          <div className="form-group">
            <label htmlFor="demote-role-select">Demote To</label>
            <select
              id="demote-role-select"
              value={demoteToRole}
              onChange={(e) => { setDemoteToRole(e.target.value); if (error) setError(''); }}
            >
              <option value="">-- Select a role --</option>
              {REGULAR_ROLES.map((role) => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
          </div>

          {demoteToRole && (
            <div className="admin-mgmt-warning">
              ⚠️ Demoting this user will remove their administrative privileges. Are you sure?
            </div>
          )}

          <div className="admin-mgmt-modal-actions">
            <button className="btn btn-secondary" onClick={() => setDemoteModalOpen(false)} disabled={actionLoading}>Cancel</button>
            <button className="btn btn-primary" onClick={handleConfirmDemote} disabled={actionLoading}>Demote User</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
